import React, { useMemo, useState } from 'react';
import { TextInput, View, TouchableOpacity } from 'react-native';
import { isValidPhoneNumber, Country } from '@oolio-group/design-common';
import { useModal } from '@oolio-group/rn-use-modal';
import type { InputPhoneProps } from './Input.types';
import { useTheme } from '../../hooks/useTheme';
import { getStyles, noFocusOutline } from './Input.styles';
import Icon from '../Icon/Icon';
import Flag from '../Flag/Flag';
import CountryModal from '../Modals/SelectCountry/CountryModal';

export const InputPhone: React.FC<InputPhoneProps> = ({
  containerStyles,
  value,
  selectedCountry,
  onCountryChange,
  onFocus,
  onBlur,
  ...props
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);

  const { showModal, closeModal } = useModal();

  const [isFocused, setIsFocused] = useState(false);

  const hasValue = !!value && String(value).length > 0;
  const isValid =
    hasValue && isValidPhoneNumber(String(value), selectedCountry.code);

  const handleSelectCountry = (nextCountry: Country) => {
    onCountryChange(nextCountry);
    closeModal();
  };

  const openCountryModal = () => {
    showModal(
      <CountryModal
        selectedCode={selectedCountry.code}
        onSelect={handleSelectCountry}
        onClose={closeModal}
      />,
      {
        // Drive the backdrop on the native driver too — the provider forces
        // `useNativeDriver` for the content, so without this the backdrop
        // fades on the JS thread and the close animation stutters.
        useNativeDriverForBackdrop: true,
        animationOut: 'slideOutDown',
        animationOutTiming: 150,
        backdropTransitionOutTiming: 0,
      },
    );
  };

  return (
    <View
      style={[
        styles.container,
        isFocused && styles.containerFocused,
        containerStyles,
      ]}
    >
      <TouchableOpacity style={styles.flag} onPress={openCountryModal}>
        <Flag code={selectedCountry.code} />
      </TouchableOpacity>
      <TextInput
        {...props}
        value={value}
        placeholderTextColor={colors.placeholder}
        keyboardType="phone-pad"
        autoCapitalize="none"
        autoComplete="tel"
        autoCorrect={false}
        style={[styles.inner, noFocusOutline]}
        onFocus={e => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={e => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />
      {hasValue && (
        <View style={styles.accessory}>
          <Icon
            size={26}
            name={isValid ? 'check' : 'exclamation-triangle'}
            color={isValid ? colors.positive : colors.negative}
          />
        </View>
      )}
    </View>
  );
};
